import React, { useState, isValidElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './RacingThemeListingAccordions.module.css';

const Chevron = ({ open }) => (
  <svg
    className={classNames(css.chevron, { [css.chevronOpen]: open })}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const AccordionIcon = ({ name }) => {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  switch (name) {
    case 'description':
      // Document with folded corner
      return (
        <svg {...common} strokeWidth={2}>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="14" y2="17" />
        </svg>
      );
    case 'howItWorks':
      // Steps / process
      return (
        <svg {...common} strokeWidth={2}>
          <circle cx="5" cy="12" r="2.5" />
          <circle cx="12" cy="12" r="2.5" />
          <circle cx="19" cy="12" r="2.5" />
          <path d="M7.5 12h2" />
          <path d="M14.5 12h2" />
        </svg>
      );
    case 'terms':
      // Scale / legal
      return (
        <svg {...common} strokeWidth={2}>
          <path d="M12 3v18" />
          <path d="M5 7h14" />
          <path d="M5 7 2 13h6L5 7z" />
          <path d="m19 7-3 6h6l-3-6z" />
        </svg>
      );
    case 'requirements':
      // Checklist
      return (
        <svg {...common} strokeWidth={2}>
          <path d="M9 6h11" />
          <path d="M9 12h11" />
          <path d="M9 18h11" />
          <path d="m4 6 1 1 2-2" />
          <path d="m4 12 1 1 2-2" />
          <path d="m4 18 1 1 2-2" />
        </svg>
      );
    case 'exposure':
      // Eye / visibility
      return (
        <svg {...common} strokeWidth={2}>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'details':
      // Text lines
      return (
        <svg {...common} strokeWidth={2}>
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="18" x2="14" y2="18" />
        </svg>
      );
    case 'shipping':
      return (
        <svg {...common} strokeWidth={2}>
          <path d="M3 7h13v8H3z" />
          <path d="M16 11h4l2 3v1h-6" />
          <circle cx="7" cy="17" r="1.5" />
          <circle cx="19" cy="17" r="1.5" />
        </svg>
      );
    case 'location':
      return (
        <svg {...common} strokeWidth={2}>
          <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
      );
    case 'about':
      // Users / team
      return (
        <svg {...common} strokeWidth={2}>
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2 20c0-3.5 3-5.5 7-5.5" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M22 20c0-2.8-2.2-4.5-5-4.8" />
        </svg>
      );
    default:
      return (
        <svg {...common} strokeWidth={2}>
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
  }
};

/**
 * Expandable listing detail rows.
 */
const RacingThemeListingAccordions = props => {
  const { items, className } = props;
  const [openIds, setOpenIds] = useState(() => (items[0] ? [items[0].id] : []));

  if (!items?.length) {
    return null;
  }

  const toggle = id => {
    setOpenIds(current =>
      current.includes(id) ? current.filter(value => value !== id) : [...current, id]
    );
  };

  return (
    <div className={classNames(css.root, className)}>
      {items.map(item => {
        const open = openIds.includes(item.id);
        const panelId = `listing-accordion-${item.id}`;
        const bodyContent = item.body;
        const isNodeBody = isValidElement(bodyContent) || Array.isArray(bodyContent);
        return (
          <div key={item.id} className={css.row}>
            <button
              type="button"
              className={css.trigger}
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => toggle(item.id)}
            >
              <span className={css.titleWrap}>
                {item.icon ? (
                  <span className={css.icon}>
                    <AccordionIcon name={item.icon} />
                  </span>
                ) : null}
                <span className={css.title}>{item.title}</span>
              </span>
              <Chevron open={open} />
            </button>
            {open ? (
              <div id={panelId} className={css.panel}>
                {isNodeBody ? (
                  <div className={css.bodyNode}>{bodyContent}</div>
                ) : (
                  <p className={css.body}>{bodyContent}</p>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

RacingThemeListingAccordions.defaultProps = {
  className: null,
  items: [],
};

RacingThemeListingAccordions.propTypes = {
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      body: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      icon: PropTypes.string,
    })
  ),
};

export default RacingThemeListingAccordions;
